import { useContext } from 'react';
import PageContext from './PageContext';
import CCOffcanvas from './CCOffcanvas.tsx';
import ClientProfileWorkflowCommentLog from './ClientProfileWorkflowCommentLog.jsx';
import ButtonComponent from './ButtonComponent.jsx';
import { Typography } from '@mui/material';

const fieldLabels = {
    payoutmethodid:   'Payout Method ID',
    achaccountnumber: 'ACH Account Number',
    achbankabanumber: 'ACH Bank ABA Number',
    achaccounttype:   'ACH Account Type'
};

// parse the stored pipe-delimited field string into an object
// e.g. "payoutmethodid: 1 | achaccountnumber: 8578643 | ..."
const parseValues = (valueStr) => {
    if (!valueStr) return {};
    let obj = {};
    valueStr.split(' | ').forEach(part => {
        let colonIdx = part.indexOf(':');
        if (colonIdx !== -1) {
            let key = part.slice(0, colonIdx).trim();
            let val = part.slice(colonIdx + 1).trim();
            obj[key] = val;
        }
    });
    return obj;
};

const ClientProfileWorkflowAuditModal = () => {
    const {
        auditModal,
        setAuditModal,
        auditRow,
        fieldDisplayNameMap,
        payoutMethodMap,
        achAccountTypeMap,
    } = useContext(PageContext);

    if (!auditRow) return null;

    const oldValues = parseValues(auditRow['oldvalue']);
    const newValues = parseValues(auditRow['newvalue']);
    const changedFields = auditRow['field'] ? auditRow['field'].split(', ').map(f => f.trim()) : [];

    // helper to get display value for a field
    const getFieldDisplayValue = (fieldKey, fieldValue) => {
        if (!fieldValue || fieldValue === 'null') return fieldValue;
        // if payoutmethodid, use payout method description instead of ID
        if (fieldKey === 'payoutmethodid' && payoutMethodMap && payoutMethodMap[fieldValue]) {
            return payoutMethodMap[fieldValue];
        }
        // if achaccounttype, use account type description instead of ID
        if (fieldKey === 'achaccounttype' && achAccountTypeMap && achAccountTypeMap[fieldValue]) {
            return achAccountTypeMap[fieldValue];
        }
        return fieldValue;
    };

    return (
        <CCOffcanvas
            title={`Audit Trail — ${auditRow['companyname'] || auditRow['mastercompanyname']}`}
            show={auditModal}
            onCanvasClose={() => setAuditModal(false)}
            bodyCssClassName="offcanvas-body bg-white p-0"
            offCanvasClassName="offcanvas-parent-card"
        >
            <div className="d-flex flex-column h-100">
                <div className="d-flex flex-column flex-grow-1 overflow-scroll">
                    {/* field changes section */}
                    <div className="p-3" style={{ borderBottom: '1px solid #ECECEC' }}>
                        <Typography sx={{ fontSize: '13px', fontFamily: 'Roboto medium', color: '#004364', mb: 1 }}>
                            Pending ACH Changes
                        </Typography>
                        <table className="w-100" style={{ fontSize: '12px', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#F7F9FA', borderBottom: '1px solid #E6EDF1' }}>
                                    <th className="p-2" style={{ color: '#6B7280', fontWeight: 500 }}>Field</th>
                                    <th className="p-2" style={{ color: '#6B7280', fontWeight: 500 }}>Old Value</th>
                                    <th className="p-2" style={{ color: '#6B7280', fontWeight: 500 }}>New Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(fieldLabels).map((key, idx) => {
                                    const isChanged = changedFields.includes(key);
                                    const displayName = fieldDisplayNameMap && fieldDisplayNameMap[key] ? fieldDisplayNameMap[key] : fieldLabels[key];
                                    const oldValue = oldValues[key] ?? 'null';
                                    const newValue = newValues[key] ?? 'null';
                                    const displayOldValue = getFieldDisplayValue(key, oldValue);
                                    const displayNewValue = getFieldDisplayValue(key, newValue);
                                    return (
                                        <tr key={key} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#F7F9FA', borderBottom: '1px solid #E6EDF1' }}>
                                            <td className="p-2" style={{ color: '#3D3D3D', fontFamily: isChanged ? 'Roboto medium' : 'inherit' }}>
                                                {displayName}
                                                {isChanged && <span className="ms-2" style={{ color: '#F59E0B', fontSize: '10px' }}>CHANGED</span>}
                                            </td>
                                            <td className="p-2" style={{ color: isChanged ? '#EF4444' : '#3D3D3D' }}>
                                                {displayOldValue}
                                            </td>
                                            <td className="p-2" style={{ color: isChanged ? '#10B981' : '#3D3D3D' }}>
                                                {displayNewValue}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* comment log section */}
                    <ClientProfileWorkflowCommentLog workflowid={auditRow['workflowid']} disableHeight={true} />
                </div>

                {/* footer close button */}
                <div className="d-flex justify-content-end p-3 w-100 offcanvas-footer z-2" style={{ height: '65px' }}>
                    <ButtonComponent
                        tag="Close"
                        border="#fcd800 1px solid"
                        background="#fff"
                        color="#001317"
                        onButtonClick={() => setAuditModal(false)}
                    />
                </div>
            </div>
        </CCOffcanvas>
    );
};


export default ClientProfileWorkflowAuditModal;
